import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@hooks/useStores';
import { useToastContext } from '@contexts/ToastContext';
import { observer } from 'mobx-react-lite';
import { CheckIcon, MailIcon } from '@components/common/ui/icons';
import { Loader } from '@components/common/ui';
import ActivationService from '@service/ActivationService';
import * as styles from '@pages/ActivationPage.module.css';

export const ActivationPage: React.FC = observer(() => {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const authStore = useAuthStore();
    const toast = useToastContext();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setErrorMessage(t('activation.tokenMissing'));
            return;
        }

        // Активация происходит на сервере через API запрос
        const activateAccount = async () => {
            try {
                // Вызываем API для активации
                await ActivationService.activate(token);

                // Обновляем данные пользователя
                await authStore.checkAuth();

                if (authStore.user?.isActivated) {
                    setStatus('success');
                    toast.success(t('activation.successToast'));
                    setTimeout(() => {
                        navigate('/');
                    }, 2000);
                } else {
                    setStatus('error');
                    setErrorMessage(t('activation.failed'));
                }
            } catch (error: any) {
                setStatus('error');
                const errorMsg =
                    error?.response?.data?.message ||
                    error?.message ||
                    t('activation.requestFailed');
                setErrorMessage(errorMsg);
                console.error('Activation error:', error);
            }
        };

        activateAccount();
    }, [token, authStore, toast, navigate, t]);

    if (status === 'loading') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <Loader variant="spinner" size="lg" text={t('activation.loading')} />
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.successState}>
                        <div className={styles.successIcon}>
                            <CheckIcon className={styles.checkIcon} />
                        </div>
                        <h1 className={styles.successTitle}>{t('activation.successTitle')}</h1>
                        <p className={styles.successText}>{t('activation.successText')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.errorState}>
                    <div className={styles.errorIcon}>
                        <MailIcon className={styles.mailIcon} />
                    </div>
                    <h1 className={styles.errorTitle}>{t('activation.errorTitle')}</h1>
                    <p className={styles.errorText}>{errorMessage}</p>
                    <button onClick={() => navigate('/')} className={styles.backButton}>
                        {t('activation.backHome')}
                    </button>
                </div>
            </div>
        </div>
    );
});
